import React, {useEffect} from 'react';
import {useCommonContext} from "../../contexts/CommonContext";
import {useI18nContext} from "../../contexts/I18nContext";
import {useStyles} from "./Topups.style";
import TopupStore from "./TopupStore";
import Box from "../../ui_components/Box/Box";
import Width from "../../ui_components/common/enums/Width";
import Alignment from "../../ui_components/common/enums/Alignment";
import Button from "../../ui_components/Button/Button";
import ShowPagination from "../../ui_components/ShowPagination/ShowPagination";
import MessageCard from "../../ui_components/MessageCard/MessageCard";
import {Observer} from "mobx-react-lite";
import TopupRow from "./TopupRow/TopupRow";
import SendToMobileIcon from '@mui/icons-material/SendToMobile';
import DateTimeRangeSelect from '../../ui_components/DateTimeRageSelect/DateTimeRangeSelect';
import NewTopupButton from "../../shared_components/Buttons/NewTopupButton/NewTopupButton";
import ButtonType from "../../ui_components/Button/ButtonType";
import CustomRangeRepository, {RangeName} from "../../ui_components/DateTimeRageSelect/CustomRangeRepository";
import FilterInput from '../../shared_components/Inputs/FilterInput/FilterInput';
import AllowAccess from '../../shared_components/Access/AllowAccess/AllowAccess';
import Access from '../../shared_components/Access/AllowAccess/Access';
import AgencySelect from '../../shared_components/Selects/AgencySelect/AgencySelect';
import Agency from '../../entities/Agency';
import TopupStatusSelect from '../../shared_components/Selects/TopupStatusSelect/TopupStatusSelect';
import DownloadTopupOrderCSVButton from '../../shared_components/Buttons/DownloadTopupOrderCSVButton/DownloadTopupOrderCSVButton';
import Checkbox from '../../ui_components/Checkbox/Checkbox';
import ExpandoMenu from '../../ui_components/ExpandoMenu/ExpandoMenu';
import ViewBatchReceiptButton from '../../shared_components/Buttons/ViewBatchReceiptButton/ViewBatchReceiptButton';
import ShowItemsSelectedAmount from '../../shared_components/Info/ShowItemsSelectedAmount/ShowItemsSelectedAmount';
import clsx from 'clsx';

const Topups: React.FC = () => {
    const commonStore = useCommonContext();
    const i18n = useI18nContext();
    const range = CustomRangeRepository.getRange(i18n, RangeName.THIS_WEEK);
    const store = new TopupStore(commonStore, range?.from?.valueOf() || undefined, range?.to?.valueOf() || undefined);
    const classes = useStyles();

    useEffect(() => {
        store.getElements();
        store.getProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAgencyChange = (newAgency?: Agency) =>
        (store.agencyFilter = newAgency);

    const onPaginationChange = async (page: number, pageSize: number) => {
        await store.goToPage(page, pageSize);
    }

    const loadElements = async () => {
        await store.getElements();
    }

    const onTopupToggleSelectAll = (value: boolean) => {
        store.selectedTopupIds = value ? store.topups.map(t => t.id) : [];
    }

    const onTopupSelect = (topupId: string) => {
        const index = store.selectedTopupIds.findIndex(t => t === topupId);
        store.selectedTopupIds = index >= 0 ?
            store.selectedTopupIds.filter(t => t !== topupId) :
            [...store.selectedTopupIds, topupId];
    }

    return (
        <Observer>
            {() => (
                <div className={classes.topups_container}>
                    <Box className={classes.filter_container} width={Width.FULL_WIDTH}
                         justifyContent={Alignment.SPACE_BETWEEN}>
                        <FilterInput value={store.filter}
                                     placeholder={i18n.translate("TOPUP_ORDER_FILTER_PLACEHOLDER")}
                                     onChange={(value) => {
                                         store.setFilter(value)
                                     }} onEnter={() => store.search()}/>
                        <DateTimeRangeSelect label={i18n.translate("TOPUP_DATE_FILTER")}
                                             selectedRange={RangeName.THIS_WEEK}
                                             onChange={(from, to) => store.setDateRangeToFilter(from, to)}
                        />
                        <AllowAccess any={[Access.SEND_ORDERS_TO_AGENCIES]}>
                            <AgencySelect selected={store.agencyFilter} onChange={onAgencyChange}/>
                        </AllowAccess>
                        <TopupStatusSelect 
                            selectedStatus={store.statusFilter}
                            onChange={(status) => store.setStatusToFilter(status)}
                        />
                        <div className={classes.filter_buttons_container}>
                            <Button
                                onClick={() => {
                                    store.search()
                                }}
                                type={ButtonType.PRIMARY}
                                className={classes.search_btn}
                                text={i18n.translate("SEARCH")}
                            />
                            <Button
                                onClick={() => {
                                    store.clearFilter()
                                }}
                                className={classes.search_btn}
                                text={i18n.translate("CLEAN")}
                            />
                        </div>
                    </Box>
                    <Box className={classes.tools_container}
                         width={Width.FULL_WIDTH}
                         justifyContent={Alignment.END}
                    >
                        <div className={classes.options_container}>
                            <Checkbox 
                                text={''}
                                selected={store.selectedTopupIds.length === store.topups.length}
                                onChange={onTopupToggleSelectAll}
                                className={classes.select_all_checkbox}/>
                            <ExpandoMenu openToRight={true} disabled={store.haveNotSelected}>
                                <>
                                    <ViewBatchReceiptButton
                                        productOrderIds={store.selectedTopupIds}
                                        disabled={
                                            new Set(
                                                store.topups
                                                .filter(t => store.selectedTopupIds.includes(t.id))
                                                .map(t => t.clientId)
                                            ).size > 1
                                    }/>
                                </>
                            </ExpandoMenu>
                            <ShowItemsSelectedAmount
                                amount={store.selectedTopupIds.length}
                                elementLabel={'envío'}
                                elementLabelPlural={'envíos'}
                            />
                            <DownloadTopupOrderCSVButton
                                from={store.dateFromFilter}
                                to={store.dateToFilter}
                                agencyId={store.agencyFilter?.id}
                                status={store.statusFilter}
                                filter={store.filter}
                                disabled={store.isEmpty}
                                type={ButtonType.SECONDARY}
                                className={clsx(classes.hide_small,classes.margin_left_download_btn)}
                            />
                        </div>
                        <NewTopupButton type={ButtonType.SECONDARY}/>
                    </Box>
                    {!store.isEmpty &&
                        <div className={classes.topups_table}>
                            <ShowPagination onChange={onPaginationChange}
                                            elementLabel={i18n.translate("TOPUPS")}
                                            pagination={store.pagination}/>
                            <div className={classes.elements_container}>
                                {store.topups.map((topup, i) => (
                                    <TopupRow
                                        key={i}
                                        topup={topup}
                                        products={store.products}
                                        filter={store.filter}
                                        selected={store.selectedTopupIds.some(t => t === topup.id)}
                                        onTopupSelect={onTopupSelect}
                                        onLoad={loadElements}
                                        from={store.dateFromFilter ?? 0}
                                        to={store.dateToFilter ?? 0}
                                    />
                                ))}
                            </div>
                            <ShowPagination
                                onChange={onPaginationChange}
                                elementLabel={i18n.translate("TOPUPS")}
                                pagination={store.pagination}/>
                        </div>}
                    {store.isEmpty &&
                        <MessageCard icon={SendToMobileIcon} title={i18n.translate("NO_TOPUPS_TITLE")}
                                     subtitle={i18n.translate("NO_TOPUPS_SUBTITLE")}/>}
                </div>
            )}
        </Observer>
    )
}

export default Topups;
